/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  createOperator,
  deleteOperator as destroyOperator,
  deleteOperators as destroyOperators,
  loadOperators,
  updateOperator,
  updateOperators
} from '@ion/core/src/services/Connect';
import {
  Entity,
  Operator,
  Operators
} from '@ion/core/src/types/entities';
import {LoadOperatorsParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {QueryKey} from '@ion/query/src/config/QueryKeys';
import {removeQueries} from '@ion/query/src/utils/helpers';
import {PageInitializeAction} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {getAccountId} from '../helpers';
import {importConfirm} from './importer';
import {toggleUserPrompt} from './user-prompt';

// name of reducer
export const NAME                            = 'adminDrivers';

// initialize drivers ...
export const INITIALIZE_ADMIN_DRIVERS        = 'INITIALIZE_ADMIN_DRIVERS';
export const RE_INITIALIZE_ADMIN_DRIVERS     = 'RE_INITIALIZE_ADMIN_DRIVERS';

// refresh grid ...
export const ADMIN_DRIVERS_GRID_REFRESHED    = 'ADMIN_DRIVERS_GRID_REFRESHED';

export const SET_GRID_COLUMNS                = 'SET_ADMIN_DRIVERS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS             = 'UPDATE_ADMIN_DRIVERS_GRID_COLUMNS';

// driver retrieval ...
export const GET_ADMIN_DRIVERS_REQUEST       = 'GET_ADMIN_DRIVERS_REQUEST';
export const GET_ADMIN_DRIVERS_RESPONSE      = 'GET_ADMIN_DRIVERS_RESPONSE';
export const GET_ADMIN_DRIVERS_ERROR         = 'GET_ADMIN_DRIVERS_ERROR';

// driver bulk import ...
export const IMPORT_ADMIN_DRIVERS            = 'IMPORT_ADMIN_DRIVERS';

// driver add ...
export const ADD_ADMIN_DRIVER                = 'ADD_ADMIN_DRIVER';
export const ADD_ADMIN_DRIVER_REQUEST        = 'ADD_ADMIN_DRIVER_REQUEST';
export const ADD_ADMIN_DRIVER_RESPONSE       = 'ADD_ADMIN_DRIVER_RESPONSE';
export const ADD_ADMIN_DRIVER_ERROR          = 'ADD_ADMIN_DRIVER_ERROR';

// driver edit ...
export const EDIT_ADMIN_DRIVER               = 'EDIT_ADMIN_DRIVER';
export const EDIT_ADMIN_DRIVER_REQUEST       = 'EDIT_ADMIN_DRIVER_REQUEST';
export const EDIT_ADMIN_DRIVER_RESPONSE      = 'EDIT_ADMIN_DRIVER_RESPONSE';
export const EDIT_ADMIN_DRIVER_ERROR         = 'EDIT_ADMIN_DRIVER_ERROR';

// manage driver associations ...
export const MANAGE_ADMIN_DRIVER             = 'MANAGE_ADMIN_DRIVER';
export const MANAGE_ADMIN_DRIVER_REQUEST     = 'MANAGE_ADMIN_DRIVER_REQUEST';
export const MANAGE_ADMIN_DRIVER_RESPONSE    = 'MANAGE_ADMIN_DRIVER_RESPONSE';
export const MANAGE_ADMIN_DRIVER_ERROR       = 'MANAGE_ADMIN_DRIVER_ERROR';

// view driver audit log ...
export const AUDIT_ADMIN_DRIVER              = 'AUDIT_ADMIN_DRIVER';
export const AUDIT_ADMIN_DRIVER_REQUEST      = 'AUDIT_ADMIN_DRIVER_REQUEST';
export const AUDIT_ADMIN_DRIVER_RESPONSE     = 'AUDIT_ADMIN_DRIVER_RESPONSE';
export const AUDIT_ADMIN_DRIVER_ERROR        = 'AUDIT_ADMIN_DRIVER_ERROR';

// driver delete ...
export const DELETE_ADMIN_DRIVER             = 'DELETE_ADMIN_DRIVER';
export const DELETE_ADMIN_DRIVER_REQUEST     = 'DELETE_ADMIN_DRIVER_REQUEST';
export const DELETE_ADMIN_DRIVER_RESPONSE    = 'DELETE_ADMIN_DRIVER_RESPONSE';
export const DELETE_ADMIN_DRIVER_ERROR       = 'DELETE_ADMIN_DRIVER_ERROR';

// bulk driver edits ...
export const EDIT_ADMIN_DRIVERS              = 'EDIT_ADMIN_DRIVERS';
export const EDIT_ADMIN_DRIVERS_REQUEST      = 'EDIT_ADMIN_DRIVERS_REQUEST';
export const EDIT_ADMIN_DRIVERS_RESPONSE     = 'EDIT_ADMIN_DRIVERS_RESPONSE';
export const EDIT_ADMIN_DRIVERS_ERROR        = 'EDIT_ADMIN_DRIVERS_ERROR';

// bulk driver deletes ...
export const DELETE_ADMIN_DRIVERS            = 'DELETE_ADMIN_DRIVERS';
export const DELETE_ADMIN_DRIVERS_REQUEST    = 'DELETE_ADMIN_DRIVERS_REQUEST';
export const DELETE_ADMIN_DRIVERS_RESPONSE   = 'DELETE_ADMIN_DRIVERS_RESPONSE';
export const DELETE_ADMIN_DRIVERS_ERROR      = 'DELETE_ADMIN_DRIVERS_ERROR';

// admin assets filter model ...
export const SET_ADMIN_DRIVERS_GRID_FILTER_MODEL = 'SET_ADMIN_DRIVERS_GRID_FILTER_MODEL';

/**
 * @initializeAdminDrivers
 */
export const initializeAdminDrivers = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_DRIVERS};
};

export const reInitializeAdminDrivers = () => ({
  type : RE_INITIALIZE_ADMIN_DRIVERS
});

/**
 * @adminDriversGridRefreshed
 */
export const adminDriversGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_DRIVERS_GRID_REFRESHED};
};

/**
 * @getAdminDrivers
 */
export const getAdminDrivers = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_DRIVERS_REQUEST});
    const data: LoadOperatorsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const {operators: items, totalCount} = await loadOperators(data);
      dispatch({type: GET_ADMIN_DRIVERS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_DRIVERS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when the Bulk Import Admin Driver Dialog is to be displayed or removed.
 */
export const importAdminDrivers = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.operator));
    dispatch({type: IMPORT_ADMIN_DRIVERS, show, result});
  };
};

/**
 * @addAdminDriver
 *
 * Invoked when the Add Admin Driver editor is to be displayed or removed.
 */
export const addAdminDriver = (show = true) => ({
  type : ADD_ADMIN_DRIVER, show
});

/**
 * @addDriver
 *
 * Invoked when a new driver is to be created, delegating to the createOperator
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addDriver = (driver: Operator) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_DRIVER_REQUEST});
    try {
      const created = await createOperator(driver, driver.schedules);
      dispatch({type: ADD_ADMIN_DRIVER_RESPONSE, driver: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.operator)));
      removeQueries(QueryKey.GET_ALL_DRIVERS, false);
    } catch (err) {
      dispatch({type: ADD_ADMIN_DRIVER_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.operator)));
      error(err);
    }
  };
};

/**
 * @editAdminDriver
 *
 * Invoked when the Edit Admin Driver editor is to be displayed or removed.
 */
export const editAdminDriver = (driver: Operator, show = true) => ({
  type : EDIT_ADMIN_DRIVER, driver, show
});

/**
 * @editDriver
 *
 * Invoked when an existing driver is to be updated, delegating to the updateOperator
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editDriver = (driver: Operator) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_DRIVER_REQUEST});
    try {
      const updated = await updateOperator(driver, driver.schedules);
      dispatch({type: EDIT_ADMIN_DRIVER_RESPONSE, driver: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.operator)));
      removeQueries(QueryKey.GET_ALL_DRIVERS, false);
    } catch (err) {
      dispatch({type: EDIT_ADMIN_DRIVER_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.operator)));
      error(err);
    }
  };
};

/**
 * @editAdminDrivers
 *
 * Invoked when the Bulk Edit Admin Driver editor is to be displayed or removed.
 */
export const editAdminDrivers = (drivers: Operators, show = true) => ({
  type : EDIT_ADMIN_DRIVERS, drivers, show
});

/**
 * @editDrivers
 *
 * Invoked when a collection of existing drivers are to be updated, delegating to
 * the updateOperators CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const editDrivers = (drivers: Operators) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_DRIVERS_REQUEST});
    try {
      const {results, errors} = await updateOperators(drivers);
      dispatch(toggleUserPrompt(successEditOptions(translate('common:entityLabels.operators'),
        {results, items: drivers})));
      dispatch({type: EDIT_ADMIN_DRIVERS_RESPONSE, drivers: results});
      removeQueries(QueryKey.GET_ALL_DRIVERS, false);
    } catch (err) {
      dispatch({type: EDIT_ADMIN_DRIVERS_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(translate('common:entityLabels.operators'))));
      error(err);
    }
  };
};

/**
 * @manageAdminDriver
 *
 * Description TBD
 */
export const manageAdminDriver = (driver: Operator, show = true) => ({
  type : MANAGE_ADMIN_DRIVER, driver, show
});

/**
 * @manageDriver
 *
 * Description TBD
 */
export const manageDriver = (driver: Operator) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: MANAGE_ADMIN_DRIVER_REQUEST});
    try {
      // [ION_SUITE TODO]: const managed = await xxxOperator(driver);
      // dispatch({type: MANAGE_ADMIN_DRIVER_RESPONSE, driver: managed});
    } catch (err) {
      dispatch({type: MANAGE_ADMIN_DRIVER_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @auditAdminDriver
 *
 * Invoked when the the driver auditor is to be displayed or removed.
 */
export const auditAdminDriver = (driver: Operator, show = true) => ({
  type : AUDIT_ADMIN_DRIVER, driver, show
});

/**
 * @auditDriver
 *
 * Invoked when a driver audit is to be made, delegating to the operatorAudit CTC
 * API and dispatching a successful response or error action accordingly.
 */
export const auditDriver = (driver: Operator) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: AUDIT_ADMIN_DRIVER_REQUEST});
    try {
      // [ION_SUITE TODO]: const audited = await xxxOperator(driver);
      // dispatch({type: AUDIT_ADMIN_DRIVER_RESPONSE, driver: audited});
    } catch (err) {
      dispatch({type: AUDIT_ADMIN_DRIVER_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @deleteAdminDriver
 *
 * Invoked when the Delete Admin Driver Dialog is to be displayed or removed.
 */
export const deleteAdminDriver = (driver: Operator, show = true) => ({
  type : DELETE_ADMIN_DRIVER, driver, show
});

/**
 * @deleteDriver
 *
 * Invoked when an existing driver is to be deleted, delegating to the destroyOperator
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteDriver = (driver: Operator) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_DRIVER_REQUEST});
    try {
      const deleted = await destroyOperator(driver);
      dispatch({type: DELETE_ADMIN_DRIVER_RESPONSE, driver: deleted});
      removeQueries(QueryKey.GET_ALL_DRIVERS, false);
    } catch (err) {
      dispatch({type: DELETE_ADMIN_DRIVER_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @deleteAdminDrivers
 *
 * Invoked when the Bulk Delete Admin Users Dialog is to be displayed or removed.
 */
export const deleteAdminDrivers = (drivers: Operators, show = true) => ({
  type : DELETE_ADMIN_DRIVERS, drivers, show
});

/**
 * @deleteDrivers
 *
 * Invoked when a collection of existing drivers are to be deleted, delegating to
 * the destroyOperators CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteDrivers = (drivers: Operators) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_DRIVERS_REQUEST});
    try {
      const {results, errors} = await destroyOperators(drivers);
      dispatch({type: DELETE_ADMIN_DRIVERS_RESPONSE, drivers: results});
      removeQueries(QueryKey.GET_ALL_DRIVERS, false);
      // [ION-APP TODO]: Error handling with errors to be implemented post UX availability ...
    } catch (err) {
      dispatch({type: DELETE_ADMIN_DRIVERS_ERROR, error: err});
      error(err);
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_DRIVERS_GRID_FILTER_MODEL, model
});
